/**
 * TriggerModal — web-side trigger UX for a deployed workflow.
 *
 * One modal, three sections:
 *   1. Required-integrations checklist (lifted from MarketplaceDetailModal
 *      pattern) — shows ✓/✗ per provider with a Connect link for missing
 *      ones. Doesn't block trigger; matches marketplace deploy behavior.
 *   2. Input form (SchemaForm reads workflow.graph.stateSchema).
 *   3. Submit → POST /projects/:id/workflows/:type/trigger.
 *
 * On 202 we navigate to /projects/:id/pipelines/:type/runs/:jobId BEFORE
 * closing the modal — the Activity tab's existing polling kicks in and
 * the user sees the new execution appear under the closing-modal animation.
 *
 * Industrial decisions:
 *  - Server is the authoritative validator (workflow-input-validator +
 *    Zod inside the runner). The client only does required-field gating
 *    so users don't waste a network round-trip on obviously-empty submits.
 *  - 400 validation errors come back as { validationErrors[], missing[],
 *    typeMismatches[], hint }. We splat them onto the form via serverErrors.
 *  - 429 (quota) is rendered as a top-banner, not as a per-field error —
 *    it's a billing concern, not an input problem.
 *  - idempotencyKey is auto-generated per modal-open via crypto.randomUUID(),
 *    sent with the trigger so a double-click can't spawn a second execution.
 *  - Unified gate: same body shape ({ input }) and same endpoint as the
 *    CLI (`zibby workflow trigger`) and cloud sub-graph dispatches. The
 *    web is just another client behind the same boundary.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { closeOutline, alertCircleOutline, checkmarkCircle } from 'ionicons/icons';
import api from '../../services/api';
import JsonInput from '../../components/shared/SchemaForm/JsonInput';
import './TriggerModal.css';

function buildServerErrorMap(payload) {
  // workflow-trigger.js returns:
  //   missing:        ['fieldA', 'fieldB']
  //   typeMismatches: [{ path: 'fieldC', expected: 'number', got: 'string' }, …]
  //   validationErrors: catch-all if more granular
  const out = {};
  for (const path of payload?.missing || []) {
    out[path] = 'Required';
  }
  for (const t of payload?.typeMismatches || []) {
    out[t.path] = `Expected ${t.expected}${t.got ? ` (got ${t.got})` : ''}`;
  }
  for (const e of payload?.validationErrors || []) {
    if (e.path && !out[e.path]) out[e.path] = e.message || e.kind || 'Invalid';
  }
  return out;
}

export default function TriggerModal({
  isOpen,
  onClose,
  pipeline,                // workflow row from GET /projects/:id/workflows/:type
  projectId,
  workflowType,
  onTriggered,             // (jobId) => void  — caller usually navigates
}) {
  const [input, setInput] = useState({});
  const [formOk, setFormOk] = useState(true);
  const [clientErrors, setClientErrors] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const [topError, setTopError] = useState(null);
  const [intStatus, setIntStatus] = useState(null);
  const [intLoading, setIntLoading] = useState(false);
  const idempotencyKeyRef = useRef(null);

  // Three-schema model: `graph.inputSchema` is the slice the user
  // supplies at trigger time (workspace/githubToken/etc. live on
  // `graph.contextSchema` and are runner-injected, NOT rendered here).
  // Deploy-time `sanitizeGraphConfig` stamps inputSchema on every row,
  // so we just read it directly — no legacy fallback.
  // Schema + source are derived from `pipeline.graph` directly, so no
  // setters needed — they update naturally when the modal opens with a
  // new pipeline prop. (Earlier versions fetched these async; that
  // legacy path is gone now that deploy-time sanitization stamps both
  // on every workflow row up front.)
  const triggerSchema = pipeline?.graph?.inputSchema || null;
  const schemaSourceField = pipeline?.graph?.inputSchema ? 'inputSchema' : null;
  const [schemaLoading] = useState(false);
  const workflowUuid = pipeline?.uuid;

  // Reset on open. New idempotency key per modal-open so a successful
  // trigger → close → reopen → trigger is two distinct executions.
  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line no-undef -- globalThis is ES2020 standard; CRA's eslint env predates it
    idempotencyKeyRef.current = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setInput({});
    setServerErrors({});
    setClientErrors({});
    setQuotaError(null);
    setTopError(null);
  }, [isOpen]);

  // Fetch required-integration status when modal opens. We use the
  // per-workflow endpoint so the response already says required[],
  // connected[], missing[] — no need to derive client-side.
  useEffect(() => {
    if (!isOpen || !workflowUuid) return;
    let cancelled = false;
    setIntLoading(true);
    api.get(`/workflows/${workflowUuid}/integrations/status`)
      .then((data) => { if (!cancelled) setIntStatus(data); })
      .catch(() => { /* missing data is fine — section won't render */ })
      .finally(() => { if (!cancelled) setIntLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, workflowUuid]);

  // Schema comes straight off pipeline.graph (initialized above) — no
  // fetch needed. The deploy pipeline stamps inputSchema + contextSchema
  // + stateSchema on the workflow row at deploy time (workflow-service.js
  // sanitizeGraphConfig), so the row IS the source of truth.

  // Esc to close (unless submitting).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  const handleJsonChange = useCallback((parsedValue, { ok, error }) => {
    setInput(parsedValue || {});
    setFormOk(ok);
    setClientErrors(error ? { _payload: error } : {});
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!formOk) return;

    setSubmitting(true);
    setQuotaError(null);
    setTopError(null);
    setServerErrors({});

    try {
      const resp = await api.post(
        `/projects/${projectId}/workflows/${workflowType}/trigger`,
        {
          input,
          idempotencyKey: idempotencyKeyRef.current,
        },
      );
      const jobId = resp?.jobId;
      if (!jobId) {
        setTopError('Trigger accepted but no jobId returned — please refresh and check Activity.');
        return;
      }
      // Navigate FIRST, then onTriggered is called for state-cleanup.
      // The caller decides what to do (typically navigate to the run page).
      onTriggered?.(jobId);
    } catch (err) {
      // api.js throws Error with .status + .body for non-2xx.
      const status = err?.status;
      const body = err?.body || err?.data || {};
      if (status === 400) {
        setServerErrors(buildServerErrorMap(body));
        if (body.hint) setTopError(body.hint);
      } else if (status === 429) {
        setQuotaError(body.quotaInfo || { message: body.error || 'Workflow execution quota exceeded' });
      } else if (status === 403) {
        setTopError('You do not have permission to trigger this workflow.');
      } else {
        setTopError(body.error || err.message || 'Failed to trigger workflow.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [submitting, formOk, projectId, workflowType, input, onTriggered]);

  if (!isOpen) return null;

  const hasRequiredIntegrations = intStatus && Array.isArray(intStatus.required) && intStatus.required.length > 0;
  const missingCount = intStatus?.missing?.length || 0;

  return (
    <div
      className="trigger-modal-overlay"
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        className="trigger-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trigger-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="trigger-modal-head">
          <h2 id="trigger-modal-title" className="trigger-modal-title">
            Trigger {pipeline?.displayName || workflowType}
          </h2>
          <button
            type="button"
            className="trigger-modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="trigger-modal-body">
          {/* ── Quota / top error banner ─────────────────────────────── */}
          {quotaError && (
            <div className="trigger-modal-banner trigger-modal-banner-warn">
              <strong>Workflow execution quota exceeded</strong>
              {quotaError.used !== undefined && quotaError.limit !== undefined && (
                <span> — {quotaError.used} / {quotaError.limit} this period.</span>
              )}{' '}
              <a href="/settings/billing" className="trigger-modal-banner-link">Upgrade plan →</a>
            </div>
          )}
          {topError && !quotaError && (
            <div className="trigger-modal-banner trigger-modal-banner-error">
              {topError}
            </div>
          )}

          {/* ── Required integrations ────────────────────────────────── */}
          {intLoading && (
            <section className="trigger-modal-section trigger-modal-section-int">
              <p className="trigger-modal-int-loading">Checking your connected integrations…</p>
            </section>
          )}
          {hasRequiredIntegrations && (
            <section className="trigger-modal-section trigger-modal-section-int">
              <h3 className="trigger-modal-section-title">
                Required integrations
                {missingCount > 0 && (
                  <span className="trigger-modal-int-badge">{missingCount} missing</span>
                )}
              </h3>
              <ul className="trigger-modal-int-list">
                {intStatus.required.map((entry) => {
                  // String entry = single integration required.
                  // { any: [...] } entry = OR group ("GitHub or GitLab").
                  if (typeof entry === 'string') {
                    const id = entry;
                    const meta = intStatus.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
                    const isConnected = (intStatus.connected || []).includes(id);
                    return (
                      <li
                        key={id}
                        className={`trigger-modal-int-row ${isConnected ? 'is-connected' : 'is-missing'}`}
                      >
                        <IonIcon
                          icon={isConnected ? checkmarkCircle : alertCircleOutline}
                          aria-hidden="true"
                          className="trigger-modal-int-icon"
                        />
                        <span className="trigger-modal-int-name">{meta.name}</span>
                        <span className="trigger-modal-int-state">
                          {isConnected ? 'Connected' : (
                            <a
                              href={meta.connectPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="trigger-modal-int-connect"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Connect →
                            </a>
                          )}
                        </span>
                      </li>
                    );
                  }
                  // OR group: satisfied if any sibling is connected.
                  const members = Array.isArray(entry?.any) ? entry.any : [];
                  if (members.length === 0) return null;
                  const groupKey = `any:${members.join(',')}`;
                  const matchedGroup = (intStatus.connected || []).find(
                    (c) => typeof c === 'object' && Array.isArray(c?.any) && c.any.join(',') === members.join(','),
                  );
                  const matched = matchedGroup?.connectedMembers || [];
                  const isConnected = matched.length > 0;
                  const label = members
                    .map((id) => intStatus.integrations?.[id]?.name || id)
                    .join(' or ');
                  return (
                    <li
                      key={groupKey}
                      className={`trigger-modal-int-row ${isConnected ? 'is-connected' : 'is-missing'}`}
                    >
                      <IonIcon
                        icon={isConnected ? checkmarkCircle : alertCircleOutline}
                        aria-hidden="true"
                        className="trigger-modal-int-icon"
                      />
                      <span className="trigger-modal-int-name">{label}</span>
                      <span className="trigger-modal-int-state">
                        {isConnected
                          ? `Connected (${matched.map((id) => intStatus.integrations?.[id]?.name || id).join(', ')})`
                          : members.map((id, i) => {
                              const meta = intStatus.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
                              return (
                                <span key={id}>
                                  {i > 0 && ' / '}
                                  <a
                                    href={meta.connectPath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="trigger-modal-int-connect"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Connect {meta.name}
                                  </a>
                                </span>
                              );
                            })}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {missingCount > 0 && (
                <p className="trigger-modal-int-note">
                  Trigger is allowed, but nodes that need a missing integration will fail at runtime.
                </p>
              )}
            </section>
          )}

          {/* ── Input (JSON textarea, schema-driven default) ─────────────
              Seed precedence:
                1. pipeline.defaultInput  — user's saved per-deployment
                   default (PATCH /workflows/:uuid {defaultInput})
                2. schema-derived example — from inputSchema defaults
              Nothing hardcoded — every template gets its own auto-
              generated payload that the user edits in place AND can
              persist via the "Save as default" button below. */}
          <section className="trigger-modal-section">
            <JsonInput
              schema={triggerSchema}
              initialValue={pipeline?.defaultInput}
              onChange={handleJsonChange}
              disabled={submitting || schemaLoading}
              initialMode="full"
            />
            {schemaSourceField === 'stateSchema' && (
              <div className="trigger-modal-banner trigger-modal-banner-warn" style={{ marginTop: 8 }}>
                <strong>Template hasn't declared an <code>inputSchema</code>.</strong>{' '}
                Showing the full runtime state — some fields below are
                injected by the runner from project config / integrations
                (workspace, githubToken, repos, …) and should be left blank
                at trigger time. Fix: export <code>inputSchema</code> from
                the template's <code>state.js</code> alongside{' '}
                <code>stateSchema</code>; rebuild + redeploy.
              </div>
            )}
            {Object.keys(serverErrors).length > 0 && (
              <div className="trigger-modal-server-errors">
                <strong>Server validation:</strong>
                <ul>
                  {Object.entries(serverErrors).map(([path, msg]) => (
                    <li key={path}><code>{path}</code> — {msg}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div className="trigger-modal-actions">
            <button
              type="button"
              className="trigger-modal-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="trigger-modal-btn-white"
              disabled={submitting || !formOk}
            >
              {submitting ? 'Triggering…' : 'Trigger'}
            </button>
          </div>

          {!formOk && clientErrors._payload && !submitting && (
            <p className="trigger-modal-hint">Fix the JSON syntax above to enable Trigger.</p>
          )}
        </form>
      </div>
    </div>
  );
}
